<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Antenna definition"
          >
            The antenna represents the communication between the spacecraft and
            a ground station. It is used in the following contexts:
            <ul>
              <li>
                <b>Power consumption</b>: the user may define a nominal power
                for the antenna, which will be be consumed continuously from the
                battery while the spacecraft is in the
                <i>antenna pointing</i> mode. This mode is activated when the
                spacecraft is visible to the ground station. The antenna power
                consumption simulation requires the mission to have the power
                and operations systems being simulated, as well as the
                spacecraft to have the power and additional systems defined.
              </li>
              <li>
                <b>Link budget</b>: the user may use it to establish a link with
                one or more ground stations, for uplink and/or downlink (as
                receiver and/or transmitter, respectively).
              </li>
            </ul>
          </exo-help-dialog>
        </template>
        <exo-input-name :root="_self" />
        <exo-input-double :root="_self" name="antennaPower" />
        <exo-input-select :root="_self" name="rfObjectType" />
        <div>
          <v-row>
            <v-col v-if="isUplinkRequiredOrOptional">
              <exo-input-select :root="_self" name="rfUplinkDefinitionType" />
              <exo-input-double :root="_self" name="gainToNoiseTemperature" />
              <exo-input-double :root="_self" name="systemNoiseTemperature">
                <tooltipSystemNoiseTemperature />
              </exo-input-double>
              <exo-input-double :root="_self" name="receiverLineLosses">
                <tooltipLineLosses />
              </exo-input-double>
              <exo-input-double :root="_self" name="receiverGain">
                <tooltipAntennaGain />
              </exo-input-double>
              <exo-input-double :root="_self" name="receiverBeamwidth">
                <tooltipBeamWidth />
              </exo-input-double>
              <exo-input-double :root="_self" name="receiverPointingPrecision">
                <tooltipPointingPrecision />
              </exo-input-double>
            </v-col>

            <v-col v-if="isDownlinkRequiredOrOptional">
              <exo-input-select :root="_self" name="rfDownlinkDefinitionType" />
              <exo-input-double :root="_self" name="eirp" />
              <exo-input-double :root="_self" name="transmittedPower">
                <tooltipTransmittedPower />
              </exo-input-double>
              <exo-input-double :root="_self" name="transmitterLineLosses">
                <tooltipLineLosses />
              </exo-input-double>
              <exo-input-double :root="_self" name="transmitterGain">
                <tooltipAntennaGain />
              </exo-input-double>
              <exo-input-double :root="_self" name="transmitterBeamwidth">
                <tooltipBeamWidth />
              </exo-input-double>
              <exo-input-double
                :root="_self"
                name="transmitterPointingPrecision"
              >
                <tooltipPointingPrecision />
              </exo-input-double>
            </v-col>
          </v-row>
        </div>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="this.modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import numericalMixin from '../../mixins/numericalForms.js'
import dataTools from '../../tools/data'
import fieldsDescription from '../../tools/fieldsDescription'
import tooltipSystemNoiseTemperature from '../../components/Tooltips/Simulations/LinkBudget/tooltipSystemNoiseTemperature.vue'
import tooltipLineLosses from '../../components/Tooltips/Simulations/LinkBudget/tooltipLineLosses.vue'
import tooltipAntennaGain from '../../components/Tooltips/Simulations/LinkBudget/tooltipAntennaGain.vue'
import tooltipBeamWidth from '../../components/Tooltips/Simulations/LinkBudget/tooltipBeamWidth.vue'
import tooltipPointingPrecision from '../../components/Tooltips/Simulations/LinkBudget/tooltipPointingPrecision.vue'
import tooltipTransmittedPower from '../../components/Tooltips/Simulations/LinkBudget/tooltipTransmittedPower.vue'

export default {
  name: 'antennaDefinition',

  mixins: [formMixin, numericalMixin],
  components: {
    tooltipSystemNoiseTemperature,
    tooltipLineLosses,
    tooltipAntennaGain,
    tooltipBeamWidth,
    tooltipPointingPrecision,
    tooltipTransmittedPower
  },

  data() {
    return {
      isDownlinkRequiredOrOptional: false,
      isUplinkRequiredOrOptional: false,
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'antennaPower',
          text: 'Power' + this.getDefaultUnitLabel('antennaPower'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,
      enumPropertiesList: this.getEnumProperties(),

      inputs: {
        antennaPower: this.getDefaultValue('antennaPower'),
        rfObjectType: this.getDefaultValue('rfObjectType'),
        rfUplinkDefinitionType: this.getDefaultValue('rfUplinkDefinitionType'),
        rfDownlinkDefinitionType: this.getDefaultValue(
          'rfDownlinkDefinitionType'
        ),
        eirp: this.getDefaultValue('eirp'),
        gainToNoiseTemperature: this.getDefaultValue('gainToNoiseTemperature'),
        systemNoiseTemperature: this.getDefaultValue('systemNoiseTemperature'),
        receiverLineLosses: this.getDefaultValue('receiverLineLosses'),
        receiverGain: this.getDefaultValue('receiverGain'),
        receiverBeamwidth: this.getDefaultValue('receiverBeamwidth'),
        receiverPointingPrecision: this.getDefaultValue(
          'receiverPointingPrecision'
        ),
        transmittedPower: this.getDefaultValue('transmittedPower'),
        transmitterLineLosses: this.getDefaultValue('transmitterLineLosses'),
        transmitterGain: this.getDefaultValue('transmitterGain'),
        transmitterBeamwidth: this.getDefaultValue('transmitterBeamwidth'),
        transmitterPointingPrecision: this.getDefaultValue(
          'transmitterPointingPrecision'
        )
      }
    }
  },

  methods: {
    getCurrentFormType() {
      return 'AntennaDefinition'
    },

    getRefList(param) {
      return []
    },

    getAntennaType(instance) {
      let types = {
        rfObjectType: instance.rfObjectType,
        rfDownlinkDefinitionType: instance.rfDownlinkDefinitionType,
        rfUplinkDefinitionType: instance.rfUplinkDefinitionType
      }
      return types
    },

    getRequiredPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getAntennaType(instance),
        'required'
      )
    },

    getOptionalPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getAntennaType(instance),
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    }
  },
  watch: {
    'inputs.rfObjectType'() {
      this.isUplinkRequiredOrOptional = this.isRequiredOrOptional(
        'rfUplinkDefinitionType',
        this.inputs
      )
      this.isDownlinkRequiredOrOptional = this.isRequiredOrOptional(
        'rfDownlinkDefinitionType',
        this.inputs
      )
    }
  }
}
</script>
